import { Column } from 'primereact/column';
import { DataTable, DataTableRowClickEvent } from 'primereact/datatable';
import React, { useState } from 'react';
import dateFormat from '../../../../utils/date-format';
import { Tag } from 'primereact/tag';
import globalCourseStore from '../../../../stores/global-course-store';
import { LectureWithSessions } from '../../../../api/types/programs/course/lectures/lecture-with-schedule-metadata';
import '../../../../component/styles/react-prime-custom-syle.css';
import { LecturePageDialog } from '../lecture-page-dialog/lecture-page-dialog';
import globalLectureBuilderStore from '../../../../stores/global-lecture-builder-store';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { lecturesDeleteOperation } from './operations/lectures-delete-operation';
import globalProgramStore from '../../../../stores/program-store';
import globalUserProfile from '../../../../stores/global-user-profile';
import { StartLectureAttendanceDialog } from './components/start-lecture-attendance-dialog';
import { startLectureAndRefreshOperation } from './operations/start-lecture-and-refresh-operation';
import { finishLectureAndRefreshOperation } from './operations/finish-lecture-and-refresh-operation';
import { LectureActivationCode } from '../../../../api/types/lecturers/details/lecture-sessions-entity';
import BubbleText, {
  BubbleType,
} from '../../../../component/bubble-text/bouble-text';

export interface LecturesListViewDatamodel {
  isProfessor: boolean;
}
export const LecturesListView: React.FC<LecturesListViewDatamodel> = observer(
  ({ isProfessor }) => {
    const lecturesRaw = globalCourseStore.getCurrentCourse()?.details?.lectures;
    const lectures = lecturesRaw
      ? [...lecturesRaw].sort(
          (a, b) =>
            new Date(a.schedule.startTime).getTime() -
            new Date(b.schedule.startTime).getTime(),
        )
      : [];
    const [showAttendanceDialog, setShowAttendanceDialog] = useState(false);
    const [selectedLectureForAttendance, setSelectedLectureForAttendance] =
      useState<LectureWithSessions | null>(null);

    const handleRowSelected = async (rowData: LectureWithSessions) => {
      globalLectureBuilderStore.selectedLecture = rowData;
    };

    const getTagStatus = (rowData: LectureWithSessions) => {
      const hasCompletedSession = rowData.sessions
        .filter((item) => item.session)
        .some((item) => item.session.startAt && item.session.endAt);

      const hasNoStartedSession = rowData.sessions
        .filter((item) => item.session)
        .every((item) => !item.session.startAt);

      const hasOngoingSession = rowData.sessions
        .filter((item) => item.session)
        .some((item) => item.session.startAt && !item.session.endAt);

      if (hasCompletedSession) {
        return <Tag severity={'success'} value={'הסתיים בהצלחה'} />;
      } else if (hasNoStartedSession) {
        return (
          <Tag
            className="neutral-tag"
            severity={'neutral'}
            value={'טרם התחיל'}
          />
        );
      } else if (hasOngoingSession) {
        return <Tag severity={'warning'} value={'התחיל וטרם נסגר'} />;
      }
    };

    const [deleteLoading, setDeleteLoading] = useState<number | null>(null);
    const handleDeleteLecture = async (lectureId: number) => {
      const currentProgramId = globalProgramStore.getCurrentId();
      const currentCourseId = globalCourseStore.getCurrentCourse()?.metadata.id;
      await lecturesDeleteOperation(
        currentProgramId,
        currentCourseId,
        lectureId,
        setDeleteLoading,
      );
    };

    // Get current user's session from lecture
    const getCurrentUserSession = (rowData: LectureWithSessions) => {
      const currentUserId = globalUserProfile.userProfile?.userWithRole?.id;
      if (!currentUserId) return null;

      return rowData.sessions
        .filter((item) => item.session && item.user)
        .find((item) => item.user.id === currentUserId);
    };

    // Check if start button should be enabled
    const canStartLecture = (rowData: LectureWithSessions): boolean => {
      const currentUserSession = getCurrentUserSession(rowData);

      // If lecture already started by current user, disable the start button
      if (currentUserSession?.session?.startAt) {
        return false;
      }

      const now = new Date();
      const startTime = new Date(rowData.schedule.startTime);
      const fiveMinutesBefore = new Date(startTime.getTime() - 5 * 60 * 1000);
      const fifteenMinutesAfter = new Date(
        startTime.getTime() + 15 * 60 * 1000,
      );

      return now >= fiveMinutesBefore && now <= fifteenMinutesAfter;
    };

    // Check if finish button should be enabled
    const canFinishLecture = (rowData: LectureWithSessions): boolean => {
      const currentUserSession = getCurrentUserSession(rowData);

      // Can't finish a lecture that never started
      if (!currentUserSession?.session?.startAt) {
        return false;
      }

      // If lecture already finished by current user, disable the finish button
      if (currentUserSession?.session?.endAt) {
        return false;
      }

      const now = new Date();
      const endTime = new Date(rowData.schedule.endTime);
      const tenMinutesAfter = new Date(endTime.getTime() + 10 * 60 * 1000);

      return now <= tenMinutesAfter;
    };

    // Handle start lecture - open dialog first, then start lecture when they submit
    const handleStartLecture = async (rowData: LectureWithSessions) => {
      // Open attendance dialog immediately (before starting lecture)
      setSelectedLectureForAttendance(rowData);
      setShowAttendanceDialog(true);
    };

    // Handle finish lecture
    const handleFinishLecture = async (rowData: LectureWithSessions) => {
      const currentProgramId = globalProgramStore.getCurrentId();
      const currentCourseId = globalCourseStore.getCurrentCourse()?.metadata.id;

      if (!currentProgramId || !currentCourseId) return;

      await finishLectureAndRefreshOperation(
        currentProgramId,
        currentCourseId,
        rowData.item.id,
      );
    };

    // Template for activation type indicators
    const activationTypeTemplate = (rowData: LectureWithSessions) => {
      if (rowData.sessions.length === 0) {
        return <div style={{ textAlign: 'right' }}></div>;
      }

      const session = rowData.sessions[0].session;
      const hasStarted =
        session.startAt !== null && session.startAt !== undefined;
      const hasFinished = session.endAt !== null && session.endAt !== undefined;

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {hasStarted && (
            <BubbleText
              type={
                session.startActivationType === LectureActivationCode.MANUAL
                  ? BubbleType.YELLOW
                  : BubbleType.GREEN
              }
              text={
                session.startActivationType === LectureActivationCode.MANUAL
                  ? 'התחילה על ידי מנהל'
                  : 'התחילה על ידי מרצה'
              }
            />
          )}
          {hasFinished && (
            <BubbleText
              type={
                session.endActivationType === LectureActivationCode.MANUAL
                  ? BubbleType.YELLOW
                  : BubbleType.GREEN
              }
              text={
                session.endActivationType === LectureActivationCode.MANUAL
                  ? 'הסתיימה על ידי מנהל'
                  : 'הסתיימה על ידי מרצה'
              }
            />
          )}
          {hasStarted && !hasFinished && (
            <BubbleText type={BubbleType.RED} text="טרם הסתיים" />
          )}
        </div>
      );
    };

    // Template for action buttons (only for professors)
    const actionTemplate = (rowData: LectureWithSessions) => {
      const currentUserSession = getCurrentUserSession(rowData);
      const hasStarted =
        currentUserSession?.session?.startAt !== null &&
        currentUserSession?.session?.startAt !== undefined;
      const hasFinished =
        currentUserSession?.session?.endAt !== null &&
        currentUserSession?.session?.endAt !== undefined;
      const canStart = canStartLecture(rowData);
      const canFinish = canFinishLecture(rowData);

      const startTooltip = canStart
        ? 'לחץ להתחלת ההרצאה'
        : 'יש להחיל הרצאה עד חמש דקות לפני ועד 15 דקות אחרי';

      const finishTooltip = canFinish
        ? 'לחץ לסיום ההרצאה'
        : 'ניתן לסגור את ההרצאה עד עשר דקות אחרי הזמן המתוכנן';

      return (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            label="התחל"
            size="small"
            tooltip={startTooltip}
            tooltipOptions={{
              showOnDisabled: true,
            }}
            severity={hasStarted ? 'secondary' : 'success'}
            disabled={!canStart}
            onClick={(e) => {
              e.stopPropagation();
              handleStartLecture(rowData);
            }}
            style={{
              minWidth: '60px',
              ...(hasStarted && {
                backgroundColor: '#6c757d',
                borderColor: '#6c757d',
                color: '#fff',
                opacity: 0.6,
              }),
            }}
          />

          <Button
            label="סיום"
            size="small"
            tooltip={finishTooltip}
            tooltipOptions={{
              showOnDisabled: true,
            }}
            severity={hasFinished ? 'secondary' : 'danger'}
            disabled={!canFinish}
            onClick={(e) => {
              e.stopPropagation();
              handleFinishLecture(rowData);
            }}
            style={{
              minWidth: '60px',
              ...(hasFinished && {
                backgroundColor: '#6c757d',
                borderColor: '#6c757d',
                color: '#fff',
                opacity: 0.6,
              }),
            }}
          />
        </div>
      );
    };

    return (
      <div>
        {!!globalLectureBuilderStore.selectedLecture && (
          <LecturePageDialog isProfessor={isProfessor} />
        )}

        <StartLectureAttendanceDialog
          visible={showAttendanceDialog}
          lecture={selectedLectureForAttendance}
          onHide={() => {
            setShowAttendanceDialog(false);
            setSelectedLectureForAttendance(null);
          }}
        />

        <DataTable
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
          }
          selectionMode={'multiple'}
          multiple={true}
          groupRowsBy="dsf"
          value={lectures}
          onRowClick={(e: DataTableRowClickEvent) => {
            handleRowSelected(e.data);
          }}
          dataKey="id"
          tableStyle={{
            direction: 'rtl',
            marginTop: '16px',
          }}
        >
          <Column
            style={{ textAlign: 'right' }}
            header="מס״ד"
            body={(rowData: LectureWithSessions, options) => (
              <div>{options.rowIndex + 1}</div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            field="נשלח ע״י"
            header="שם ההרצאה"
            body={(rowData: LectureWithSessions) => (
              <div>{rowData.item.lectureName}</div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            field="מחכים מ"
            header="תאריך"
            body={(rowData: LectureWithSessions) => (
              <div>
                {dateFormat.displayFormatDate(rowData.schedule.startTime)}
              </div>
            )}
          />

          <Column
            style={{ textAlign: 'right' }}
            header="יום בשבוע"
            body={(rowData: LectureWithSessions) => (
              <div>
                {new Date(rowData.schedule.startTime).toLocaleDateString(
                  'he-IL',
                  { weekday: 'long' },
                )}
              </div>
            )}
          />

          <Column
            style={{ textAlign: 'right' }}
            field="סטטוס"
            header="שעות"
            body={(rowData: LectureWithSessions) => (
              <div>
                {dateFormat.formatTimeRangeBetweenDates(
                  rowData.schedule.endTime,
                  rowData.schedule.startTime,
                )}
              </div>
            )}
          />

          <Column
            style={{ textAlign: 'right' }}
            field="מחכים מ"
            header="כיתה"
            body={(rowData: LectureWithSessions) => (
              <div>{rowData.class.className}</div>
            )}
          />
          <Column
            style={{ textAlign: 'right' }}
            field="סטטוס"
            header="סטטוס"
            body={(rowData: LectureWithSessions) => (
              <div>{getTagStatus(rowData)}</div>
            )}
          />

          <Column
            style={{ textAlign: 'right' }}
            header="סוג הפעלה"
            body={activationTypeTemplate}
          />

          {isProfessor && (
            <Column
              style={{ textAlign: 'center' }}
              header="פעולות"
              body={actionTemplate}
            />
          )}

          <Column
            style={{ textAlign: 'right' }}
            field="נשלח ע״י"
            header=""
            body={(rowData: LectureWithSessions) => (
              <Button
                icon="pi pi-trash"
                text
                severity="danger"
                tooltip={'מחיקת הרצאה'}
                loading={deleteLoading === rowData.item.id}
                disabled={deleteLoading !== null}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteLecture(rowData.item.id);
                }}
              />
            )}
          />
        </DataTable>
      </div>
    );
  },
);
